/**
 * Weapon kinds (ประเภทอาวุธ): what a weapon item is, so lists, stores and the 3D models can tell a
 * sabre from a crossbow. The reference is docs/art/weapons-ref. The names match the 2D rig's own
 * kinds (apps/client render/rig/weapons.ts `WeaponKind`, all of which are here), plus four it does
 * not draw: crossbow, ring, sceptre and shield. The 2D figures still draw the weapon of their art's
 * look whatever the item's kind, so no new kind needs drawing until the 3D models exist. A shield
 * is worn in the weapon slot: its bearer fights with shield and fist.
 */
export type WeaponKind =
  | 'jian'
  | 'dao'
  | 'spear'
  | 'qiang'
  | 'bow'
  | 'fist'
  | 'umbrella'
  | 'fan'
  | 'kris'
  | 'staff'
  | 'spike'
  | 'chain'
  | 'glaive'
  | 'halberd'
  | 'axe'
  | 'hammer'
  | 'whisk'
  | 'hook'
  | 'whip'
  | 'flute'
  | 'brush'
  | 'crossbow'
  | 'ring'
  | 'sceptre'
  | 'shield';

export interface WeaponKindDef {
  /** The family's Thai name, as on the reference sheet. */
  readonly label: string;
  /** Which hands hold it: the right, both (a pair), the left (a bow, a shield), or wraps on the fists. */
  readonly hands: 'right' | 'pair' | 'left' | 'fists';
}

export const WEAPON_KINDS: Readonly<Record<WeaponKind, WeaponKindDef>> = {
  jian: { label: 'กระบี่', hands: 'right' },
  dao: { label: 'ดาบ', hands: 'right' },
  spear: { label: 'หอก', hands: 'right' },
  qiang: { label: 'ทวน', hands: 'right' },
  bow: { label: 'ธนู', hands: 'left' },
  fist: { label: 'กรงเล็บ', hands: 'fists' },
  umbrella: { label: 'ร่ม', hands: 'right' },
  fan: { label: 'พัด', hands: 'right' },
  kris: { label: 'มีดสั้น', hands: 'right' },
  staff: { label: 'กระบอง', hands: 'right' },
  spike: { label: 'หนาม', hands: 'pair' },
  chain: { label: 'ลูกตุ้ม', hands: 'right' },
  glaive: { label: 'ง้าว', hands: 'right' },
  halberd: { label: 'ทวนเสี้ยวจันทร์', hands: 'right' },
  axe: { label: 'ขวาน', hands: 'right' },
  hammer: { label: 'ค้อน', hands: 'pair' },
  whisk: { label: 'แส้ปัดฝุ่น', hands: 'right' },
  hook: { label: 'ขอเกี่ยว', hands: 'pair' },
  whip: { label: 'แส้', hands: 'right' },
  flute: { label: 'ขลุ่ย', hands: 'right' },
  brush: { label: 'พู่กัน', hands: 'right' },
  crossbow: { label: 'หน้าไม้', hands: 'right' },
  ring: { label: 'ห่วง', hands: 'pair' },
  sceptre: { label: 'คทา', hands: 'right' },
  shield: { label: 'โล่', hands: 'left' },
};

export const WEAPON_KIND_LIST = Object.keys(WEAPON_KINDS) as readonly WeaponKind[];
